import type { LocaleObject } from '@nuxtjs/i18n'
import type { INews } from '~/components/entity/news'

type TNewsLanguage = 'ru' | 'ky'

/**
 * Canonical and hreflang links of a news page.
 *
 * The Russian and the Kyrgyz version of a news item are separate records, and every
 * record opens under both locale prefixes. The canonical URL is the one in the language
 * of the content (ru → /news/x, ky → /ky/news/x-kyr), and the translation, if there is
 * one, is linked with hreflang — the same URLs the backend sitemap lists.
 */
export const useNewsLanguageLinks = (news: Ref<INews | undefined> | ComputedRef<INews | undefined>) => {
  const { locales } = useI18n()
  const config = useRuntimeConfig()
  const baseUrl = config.public.siteUrl as string
  const { disableGenericAlternateLinks } = useAlternateLinks()

  // The links below replace the generic ones; without a translation the generic links
  // would point to /ky/news/{ru-slug}, the same Russian text under the other prefix
  disableGenericAlternateLinks()

  // Older records have no language; their Kyrgyz version has the "-kyr" slug suffix
  const languageOf = (item: INews): TNewsLanguage =>
    item.language ?? (item.slug.endsWith('-kyr') ? 'ky' : 'ru')

  const localizedUrl = (path: string, language: TNewsLanguage) =>
    baseUrl + encodeURI((language === 'ky' ? '/ky' : '') + path)

  const isoOf = (language: TNewsLanguage) =>
    (locales.value as LocaleObject[]).find((loc) => loc.code === language)?.iso || language

  useHead(() => {
    const item = unref(news)
    if (!item?.url) return {}

    const language = languageOf(item)
    const link = [{ key: 'canonical', rel: 'canonical', href: localizedUrl(item.url, language) }]

    if (item.alternative_link) {
      // The translation is the same kind of page (news or article) with its own slug
      const translationUrl = item.url.replace(/[^/]+$/, item.alternative_link)
      const urls: Record<TNewsLanguage, string> = {
        ru: localizedUrl(language === 'ru' ? item.url : translationUrl, 'ru'),
        ky: localizedUrl(language === 'ky' ? item.url : translationUrl, 'ky')
      }

      link.push(
        ...(['ru', 'ky'] as const).map((code) => ({
          key: `alternate-${code}`,
          rel: 'alternate',
          hreflang: isoOf(code),
          href: urls[code]
        })),
        { key: 'alternate-x-default', rel: 'alternate', hreflang: 'x-default', href: urls.ru }
      )
    }

    return { link }
  })
}
